import React from "react";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";

/**
 * Figma node 8094:167651 — Logout confirmation modal.
 */
const LogoutConfirmModal = ({ open = false, onClose, onConfirm }) => (
  <Modal
    open={open}
    onClose={onClose}
    closeAfterTransition
    aria-labelledby="logout-confirm-title"
    BackdropProps={{
      sx: {
        backgroundColor: "rgba(17, 24, 39, 0.45)",
        backdropFilter: "blur(4px)",
      },
    }}
  >
    <Fade in={open} timeout={280}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "calc(100% - 32px)", sm: 430 },
          maxWidth: "95vw",
          outline: "none",
          "@media (prefers-reduced-motion: reduce)": {
            "& .logoutModalCard, & .logoutModalIcon, & .logoutModalContent": {
              animation: "none !important",
            },
          },
        }}
      >
        <Box
          className="logoutModalCard"
          sx={{
            bgcolor: "#fff",
            border: "1.5px solid #E5E7EB",
            borderRadius: "28px",
            boxShadow: "0px 18px 48px -12px rgba(0,0,0,0.1)",
            backdropFilter: "blur(9px)",
            px: { xs: 3, sm: "21.5px" },
            py: { xs: 4, sm: 5 },
            animation: "logoutModalPop 0.38s cubic-bezier(0.22, 1, 0.36, 1)",
            "@keyframes logoutModalPop": {
              "0%": { opacity: 0, transform: "scale(0.92) translateY(12px)" },
              "100%": { opacity: 1, transform: "scale(1) translateY(0)" },
            },
          }}
        >
          <Stack alignItems="center" spacing={0} sx={{ textAlign: "center" }}>
            <Box
              className="logoutModalIcon"
              sx={{
                position: "relative",
                width: 90,
                height: 90,
                mb: "36px",
                flexShrink: 0,
                animation: "logoutIconPop 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.08s both",
                "@keyframes logoutIconPop": {
                  "0%": { opacity: 0, transform: "scale(0.7)" },
                  "70%": { opacity: 1, transform: "scale(1.06)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
              }}
            >
              <Box
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  border: "1.125px solid rgba(255,23,68,0.3)",
                  boxShadow: "0px 0px 45px 0px rgba(255,23,68,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "rgba(147,0,10,0.07)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src="/images/profile-v2/log-out-icon.svg"
                    alt=""
                    sx={{ width: 43, height: 43 }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  top: -9,
                  right: -9,
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  bgcolor: "#FF1744",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: -13.5,
                  top: "calc(50% + 3.38px)",
                  transform: "translateY(-50%)",
                  width: 6.75,
                  height: 6.75,
                  borderRadius: "50%",
                  bgcolor: "#FF1744",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: -4.5,
                  width: 11.25,
                  height: 11.25,
                  borderRadius: "50%",
                  border: "1.125px solid rgba(255,23,68,0.4)",
                }}
              />
            </Box>

            <Stack
              className="logoutModalContent"
              spacing="30px"
              alignItems="center"
              sx={{
                width: "100%",
                animation: "logoutContentIn 0.4s ease 0.14s both",
                "@keyframes logoutContentIn": {
                  "0%": { opacity: 0, transform: "translateY(10px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <Stack spacing={1} alignItems="center">
                <Text
                  id="logout-confirm-title"
                  sx={{
                    fontSize: { xs: 24, sm: 28 },
                    color: "#111827",
                    letterSpacing: "-0.9px",
                    lineHeight: { xs: 1.3, sm: "46.8px" },
                  }}
                >
                  Logout
                </Text>
                <Text
                  sx={{
                    fontSize: { xs: 16, sm: 18 },
                    color: "#000",
                    lineHeight: "20px",
                  }}
                >
                  Are you sure you want to logout?
                </Text>
              </Stack>

              <Stack spacing="18px" alignItems="center" sx={{ width: "100%" }}>
                <Box
                  component="button"
                  type="button"
                  onClick={onConfirm}
                  sx={{
                    width: "100%",
                    height: 52,
                    borderRadius: "12px",
                    border: "1px solid #2563EB",
                    background:
                      "linear-gradient(167deg, #1D4ED8 0%, #2563EB 100%)",
                    boxShadow: "0px 10px 24px -8px rgba(29,78,216,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    cursor: "pointer",
                    font: "inherit",
                    p: 1.5,
                    "&:hover": { filter: "brightness(1.05)" },
                  }}
                >
                  <Text sx={{ fontWeight: 500, fontSize: 18, color: "#fff" }}>
                    Logout
                  </Text>
                  <ArrowForwardIcon sx={{ fontSize: 24, color: "#fff" }} />
                </Box>

                <Box
                  component="button"
                  type="button"
                  onClick={onClose}
                  sx={{
                    border: 0,
                    bgcolor: "transparent",
                    cursor: "pointer",
                    font: "inherit",
                    p: 0,
                    width: "100%",
                  }}
                >
                  <Text
                    sx={{
                      fontWeight: 500,
                      fontSize: 16,
                      color: "#000",
                      textAlign: "center",
                      lineHeight: "17.3px",
                    }}
                  >
                    Back
                  </Text>
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Fade>
  </Modal>
);

export default LogoutConfirmModal;
